import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import { alpha, Box, Button, Paper, Tooltip, Typography } from "@mui/material"
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridApiPro,
  GridColDef,
  GridEventListener,
  gridExpandedSortedRowIdsSelector,
  GridFilterModel,
  gridFilterModelSelector,
  GridInitialState,
  GridRowId,
} from "@mui/x-data-grid-pro"
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { StudentDetailPanel } from "../studentDetailPanel"
import { ProgramStatus } from "@features/students-tab/studentsTab.slice"
import { StudentAcademicStatusDTO } from "../studentAcademicStatus.dto"
import appTheme from "../../../theme/appTheme"
import { ColumnKeys } from "./columns/column.keys"
import { buildStudentsTableColumns } from "./columns/studentsTable.columns"
import { studentsGridIconSlots } from "./filters/students.table.toolbar"
import { getActionsColumn } from "./studentsTable.actions"
import { FilterErrorBoundary } from "./table.error.boundary"

// ".v2" = the column-diet defaults, ".v3" = the viewport-fit widths, ".v4" =
// the always-visible Actions pair, ".v5" = scores out of the default set
// (the grid finally fits every common window). Bumping the prefix orphans
// every older saved layout in one stroke — those layouts carry the columns
// and widths the newer defaults exist to replace, so migrating them in place
// would defeat the reset.
const STORAGE_KEY = "studentsTableGridState.v5"

// Columns the column diet killed as defaults on every tab. Their fields stay
// defined (search matches hidden fields, saved layouts can carry them); the
// data they held lives in the row's expanded panel.
const HIDDEN_ON_EVERY_TAB = [
  // Folded into the two-line Name cell.
  ColumnKeys.EMAIL,
  // Raw 11-digit number nobody reads; kebab/panel deep-link. Opted back in,
  // the cell renders as a contact hyperlink.
  ColumnKeys.HUBSPOT_ID,
  // Career-coach metric; panel material.
  ColumnKeys.LAST_INTERVIEW_SCORE,
  // School / Cohort / Class fold into the Enrollment composite.
  ColumnKeys.PROGRAM,
  ColumnKeys.COHORT,
  ColumnKeys.REGISTERED_CLASS,
  // Panel identity chip.
  ColumnKeys.LANGUAGE,
  // Raw scores live in the panel header (`CSAT 2 · 17mo`); the Attention
  // chips already flag bad fresh scores per row. As default columns they
  // were what kept pushing the grid past the viewport at common widths.
  ColumnKeys.LAST_CSAT_SCORE,
  ColumnKeys.LAST_NPS_SCORE,
]

// Each tab answers a different question, so each gets its own defaults:
// Upcoming = logistics (who starts next; score columns are structurally empty
// pre-start), Active = intervention (flagged rows first), Ended = the
// historical record (no stale progress, no ended mentorship).
// Mentor is role-aware: pure noise for a pure mentor (always "me" under the
// my-students default), routing signal for PMs/ops.
function buildTabDefaults(
  programStatus: ProgramStatus,
  isPureMentor: boolean,
): {
  hiddenColumns: string[]
  sortModel: { field: string; sort: "asc" | "desc" }[]
} {
  const mentorColumnDefault = isPureMentor ? [ColumnKeys.MENTOR] : []
  // Narrow windows drop the lowest-priority defaults instead of overflowing
  // sideways (a permanently-overflowing grid pans under the pinned band on
  // any diagonal trackpad scroll). Thresholds = the column-width budget plus
  // the ~288px of chrome left of the grid; mount-time width is enough since
  // defaults only apply before a saved layout exists.
  const narrowHidden =
    window.innerWidth < 1360
      ? [ColumnKeys.MENTOR, ColumnKeys.LAST_LEARNING_ACTIVITY]
      : window.innerWidth < 1490
      ? [ColumnKeys.MENTOR]
      : []
  switch (programStatus) {
    case "upcoming":
      return {
        hiddenColumns: [
          ...HIDDEN_ON_EVERY_TAB,
          ...mentorColumnDefault,
          ...narrowHidden,
          ColumnKeys.LAST_CSAT_SCORE,
          ColumnKeys.LAST_NPS_SCORE,
          ColumnKeys.LAST_LEARNING_ACTIVITY,
        ],
        sortModel: [{ field: ColumnKeys.START_DATE, sort: "asc" }],
      }
    case "active":
      return {
        hiddenColumns: [
          ...HIDDEN_ON_EVERY_TAB,
          ...mentorColumnDefault,
          ...narrowHidden,
          ColumnKeys.START_DATE,
        ],
        // Triage order on a VISIBLE column: most signals first. The old
        // csat-ascending default sorted by a column that no longer renders.
        sortModel: [{ field: ColumnKeys.ATTENTION, sort: "desc" }],
      }
    case "ended":
      return {
        hiddenColumns: [
          ...HIDDEN_ON_EVERY_TAB,
          ...narrowHidden,
          ColumnKeys.START_DATE,
          ColumnKeys.LAST_LEARNING_ACTIVITY,
          // Alumni CSAT is the exit record; a second score column would push
          // Outcome out of the viewport for nothing.
          ColumnKeys.LAST_NPS_SCORE,
          ColumnKeys.MENTOR,
        ],
        sortModel: [{ field: ColumnKeys.COHORT, sort: "desc" }],
      }
  }
}

// The grid area is a flex-filled stage: it grows to take ALL the height the
// page column leaves below the chrome, so it reaches the viewport bottom with
// no dead band — and it does so WITHOUT a fixed viewport-minus-constant height,
// which went stale the moment the chrome above it got shorter (Mayer caught the
// blank space this left). The height is still stable across the tab-switch
// REMOUNT (the key below) because it derives from the parent layout, not the
// grid's content — so loading, empty and loaded states swap inside a fixed-size
// box without the area collapsing. minHeight:0 lets the grid scroll internally.
const GRID_STAGE_SX = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
} as const

export const StudentsTableGrid = (props: {
  data: StudentAcademicStatusDTO[] | null
  // While the fetch is in flight the grid shows its loading overlay instead
  // of a false "No rows".
  loading: boolean
  programStatus: ProgramStatus
  isPureMentor: boolean
  expandedStudentId: string | null
  // Owned by the page so the Filter control, Export control and applied-filter
  // chips can drive the grid from outside the grid's own DOM.
  apiRef: MutableRefObject<GridApiPro>
  // The Filter control on the band: the filter panel anchors onto it, so it
  // opens under the control that summoned it.
  panelAnchorEl: HTMLElement | null
  // Saved-views handshake: a bumped seq means a view was applied and a grid
  // filter (or an explicit clear) is waiting in the parent's one-shot ref.
  gridFilterSeq: number
  consumePendingGridFilter: () => GridFilterModel | null | undefined
  onGridFilterChanged: (model: GridFilterModel) => void
  onExpandedStudentChanged: (studentId: string | null) => void
  onManageProgramsClicked: (studentId: string, studentName: string) => void
}) => {
  const {
    data,
    loading,
    programStatus,
    isPureMentor,
    expandedStudentId,
    apiRef,
    panelAnchorEl,
    gridFilterSeq,
    consumePendingGridFilter,
    onGridFilterChanged,
    onExpandedStudentChanged,
    onManageProgramsClicked,
  } = props
  const [initialState, setInitialState] = useState<GridInitialState>()
  // Per-tab slot: a saved Active layout must not leak into Upcoming/Ended.
  const storageKey = `${STORAGE_KEY}:${programStatus}`

  // Selection exists for one job: grabbing a set of emails to contact. The
  // action rides a floating bar anchored to the grid (the attendance painter
  // bar idiom) — the control lives where the selection lives, not in the
  // page's filter row.
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [copiedCount, setCopiedCount] = useState<number | null>(null)
  useEffect(() => {
    // The DataGridPro below remounts with an empty selection on tab/role
    // switches (its key) and never reports that reset — mirror it here so
    // the bar can't survive with a stale count.
    setSelectedIds([])
    setCopiedCount(null)
  }, [programStatus, isPureMentor])
  const selectedEmails = useMemo(() => {
    const selected = new Set(selectedIds)
    return (data ?? [])
      .filter((row) => selected.has(row.studentId))
      .map((row) => row.contactDetails?.email)
      .filter((email): email is string => Boolean(email))
  }, [data, selectedIds])
  const copySelectedEmails = () => {
    navigator.clipboard.writeText(selectedEmails.join(", ")).then(() => {
      setCopiedCount(selectedEmails.length)
      window.setTimeout(() => setCopiedCount(null), 2500)
    })
  }

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState()
      // The snapshot is a LAYOUT memory, not a session memory: exportState
      // also captures the preference panel (the filter editor) being open,
      // and restoring that reopens the panel over the grid on every load —
      // Mayer's "dropdowns are uncollapsed".
      delete currentState.preferencePanel
      localStorage.setItem(storageKey, JSON.stringify(currentState))
    }
  }, [apiRef, storageKey])

  const actionsColumn = getActionsColumn(onManageProgramsClicked)

  // Discoverability: the drag-select gesture is invisible until you know it
  // exists. Replacing the implicit checkbox column with a tooltip-wrapped copy
  // keeps behavior identical (same field, same renderers) and teaches the
  // gesture on hover.
  const checkboxColumn: GridColDef = {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    renderHeader: (params) => (
      <Tooltip title="Select all · or drag down the checkboxes to select a range">
        <span style={{ display: "inline-flex" }}>
          {GRID_CHECKBOX_SELECTION_COL_DEF.renderHeader?.(params)}
        </span>
      </Tooltip>
    ),
    renderCell: (params) => (
      <Tooltip
        title="Drag down to select a range · shift-click extends"
        enterDelay={700}
      >
        <span style={{ display: "inline-flex" }}>
          {GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params)}
        </span>
      </Tooltip>
    ),
  }
  const tabDefaults = buildTabDefaults(programStatus, isPureMentor)

  // Drag-to-select down the checkbox column: mousedown on a checkbox cell
  // anchors, every row the pointer sweeps extends a contiguous range (visual
  // order), mouseup freezes it. The range is ADDED to whatever was selected
  // before the drag, so disjoint ranges compose; dragging back toward the
  // anchor shrinks the range honestly (selection = base ∪ current range).
  // Shift-click range selection and plain-click toggling stay native MUI.
  const dragRef = useRef<{
    anchorId: GridRowId
    baseSelection: GridRowId[]
    moved: boolean
  } | null>(null)
  // A drag that swept rows must not end as a row click (common-ancestor click
  // fires when mousedown and mouseup land on different rows) — that would
  // toggle a detail panel the user never asked for.
  const suppressRowClickRef = useRef(false)
  // Edge auto-scroll (see the drag effect): rowMouseEnter fires only for
  // MOUNTED rows, so a drag toward the bottom of the virtual scroller stalls at
  // the last visible row. These drive a rAF loop that scrolls the scroller
  // while the pointer sits in an edge zone and extends the range onto the rows
  // that mount under it — the one-gesture reach the attendance painter has.
  const scrollerRef = useRef<HTMLElement | null>(null)
  const gridWrapperRef = useRef<HTMLDivElement | null>(null)
  const dragPointerRef = useRef<{ x: number; y: number } | null>(null)
  const autoScrollFrameRef = useRef(0)

  useEffect(() => {
    // Before the grid's first mount (the initialState loading return) the api
    // ref holds an empty object — nothing to subscribe to yet.
    if (typeof apiRef.current?.subscribeEvent !== "function") {
      return
    }
    // Extend the live drag range onto a row, whether the pointer swept over it
    // (rowMouseEnter) or it mounted under a held-still pointer while the edge
    // loop scrolled — the two compose, because rowMouseEnter can't fire for a
    // row that was never rendered. The range is ADDED to the pre-drag
    // selection, so disjoint ranges accumulate and dragging back toward the
    // anchor shrinks honestly (selection = base ∪ current range).
    const extendDragToRow = (rowId: GridRowId) => {
      const drag = dragRef.current
      if (!drag) {
        return
      }
      if (rowId === drag.anchorId && !drag.moved) {
        return
      }
      drag.moved = true
      const sortedIds = gridExpandedSortedRowIdsSelector(apiRef)
      const anchorIndex = sortedIds.indexOf(drag.anchorId)
      const currentIndex = sortedIds.indexOf(rowId)
      if (anchorIndex < 0 || currentIndex < 0) {
        return
      }
      const [from, to] =
        anchorIndex <= currentIndex
          ? [anchorIndex, currentIndex]
          : [currentIndex, anchorIndex]
      const range = sortedIds.slice(from, to + 1)
      apiRef.current.setRowSelectionModel(
        Array.from(new Set([...drag.baseSelection, ...range])),
      )
    }
    // The rAF edge-scroll loop: while a drag is live and the pointer sits in
    // the top/bottom edge zone of the virtual scroller, scroll it — speed ramps
    // with proximity so the very edge covers a long roster fast while the inner
    // edge stays precise — then re-resolve the row now under the pointer so the
    // range keeps growing even with the pointer held perfectly still. A frame
    // loop (not a pointermove side effect) because a stationary pointer past
    // the edge fires no more moves. Mirrors the attendance painter's 48px zone.
    const EDGE_ZONE = 48
    const MIN_SPEED = 6
    const MAX_SPEED = 24
    const runAutoScroll = () => {
      const scroller = scrollerRef.current
      const pointer = dragPointerRef.current
      if (dragRef.current && scroller && pointer) {
        const rect = scroller.getBoundingClientRect()
        const topGap = pointer.y - rect.top
        const bottomGap = rect.bottom - pointer.y
        let delta = 0
        if (topGap < EDGE_ZONE) {
          const ramp = Math.min(1, (EDGE_ZONE - topGap) / EDGE_ZONE)
          delta = -(MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ramp)
        } else if (bottomGap < EDGE_ZONE) {
          const ramp = Math.min(1, (EDGE_ZONE - bottomGap) / EDGE_ZONE)
          delta = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ramp
        }
        if (delta !== 0) {
          scroller.scrollTop += delta
          // Re-resolve the row now under the pointer, clamped INTO the
          // scroller so a pointer held past the edge still lands on the
          // bottom-most (or top-most) visible row — the same domain clamp the
          // rectangle painter does. Deterministic, so the range advances even
          // if no mouseenter fires under a perfectly stationary pointer.
          const rx = Math.min(
            Math.max(pointer.x, rect.left + 1),
            rect.right - 1,
          )
          const ry = Math.min(
            Math.max(pointer.y, rect.top + 1),
            rect.bottom - 1,
          )
          const rowId = document
            .elementFromPoint(rx, ry)
            ?.closest<HTMLElement>(".MuiDataGrid-row")
            ?.getAttribute("data-id")
          if (rowId != null) {
            extendDragToRow(rowId)
          }
        }
      }
      autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll)
    }
    const handleCellMouseDown: GridEventListener<"cellMouseDown"> = (
      params,
      event,
    ) => {
      // Drags start ONLY inside the checkbox cell — everywhere else the
      // press stays a click (row click expands, cell widgets keep working).
      if (params.field !== GRID_CHECKBOX_SELECTION_FIELD) {
        return
      }
      if (event.button !== 0) {
        return
      }
      dragRef.current = {
        anchorId: params.id,
        baseSelection: Array.from(apiRef.current.getSelectedRows().keys()),
        moved: false,
      }
      // Resolve the scroller of THIS grid instance (it remounts per tab) and
      // seed the pointer so the loop has coordinates before the first move.
      scrollerRef.current =
        gridWrapperRef.current?.querySelector<HTMLElement>(
          ".MuiDataGrid-virtualScroller",
        ) ?? null
      dragPointerRef.current = { x: event.clientX, y: event.clientY }
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll)
      // No text selection while sweeping rows; the checkbox still toggles on
      // plain clicks (click fires regardless of a prevented mousedown).
      event.preventDefault()
    }
    const handleRowMouseEnter: GridEventListener<"rowMouseEnter"> = (
      params,
    ) => {
      extendDragToRow(params.id)
    }
    const handleWindowMouseMove = (event: MouseEvent) => {
      if (dragRef.current) {
        dragPointerRef.current = { x: event.clientX, y: event.clientY }
      }
    }
    const handleWindowMouseUp = () => {
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      dragPointerRef.current = null
      if (dragRef.current?.moved) {
        suppressRowClickRef.current = true
        // The click the browser synthesizes from this mouseup dispatches
        // before timeouts run, so the flag covers exactly that one click.
        window.setTimeout(() => {
          suppressRowClickRef.current = false
        }, 0)
      }
      dragRef.current = null
    }
    const unsubscribeCellMouseDown = apiRef.current.subscribeEvent(
      "cellMouseDown",
      handleCellMouseDown,
    )
    const unsubscribeRowMouseEnter = apiRef.current.subscribeEvent(
      "rowMouseEnter",
      handleRowMouseEnter,
    )
    window.addEventListener("mousemove", handleWindowMouseMove)
    window.addEventListener("mouseup", handleWindowMouseUp)
    return () => {
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      unsubscribeCellMouseDown()
      unsubscribeRowMouseEnter()
      window.removeEventListener("mousemove", handleWindowMouseMove)
      window.removeEventListener("mouseup", handleWindowMouseUp)
    }
    // The grid remounts on key change (tab / role): resubscribe to the fresh
    // grid instance the same way the mount effect would.
  }, [apiRef, programStatus, isPureMentor, initialState])

  // Applying a saved view may carry a grid filter (or an explicit clear).
  // The pending model is consumed AFTER the grid (re)mounts — a view that
  // switches tabs remounts the grid, so the filter must land on the new
  // instance, not the dying one. The same pass reports the live model up, so
  // the views bar also sees filters restored from the saved layout, not only
  // user edits.
  useEffect(() => {
    if (typeof apiRef.current?.setFilterModel !== "function") {
      return
    }
    const pending = consumePendingGridFilter()
    if (pending !== undefined) {
      apiRef.current.setFilterModel(pending ?? { items: [] })
    }
    onGridFilterChanged(gridFilterModelSelector(apiRef))
  }, [
    apiRef,
    gridFilterSeq,
    initialState,
    consumePendingGridFilter,
    onGridFilterChanged,
  ])

  useLayoutEffect(() => {
    // Only current-version keys are read; every save since v2 was exported
    // with the checkbox and detail-toggle columns already pinned, so no
    // repair pass is needed (the old one existed for saves that predated the
    // inline panel / selection).
    const stateFromLocalStorage = localStorage?.getItem(storageKey)
    const restored: GridInitialState = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : {}
    // Heal snapshots saved before preferencePanel was stripped at save time —
    // an open-filter-panel flag in an old snapshot must not reopen the panel.
    delete restored.preferencePanel
    // One splice, not a reset: v2 saves that predate the Attention column
    // would append it at the far right; put it in its spec slot, right after
    // Last Progress, without touching the rest of the saved layout.
    const orderedFields = restored.columns?.orderedFields
    if (
      programStatus === "active" &&
      orderedFields &&
      !orderedFields.includes(ColumnKeys.ATTENTION)
    ) {
      const anchor = orderedFields.indexOf(ColumnKeys.LAST_LEARNING_ACTIVITY)
      if (anchor >= 0) {
        orderedFields.splice(anchor + 1, 0, ColumnKeys.ATTENTION)
      }
    }
    setInitialState(restored)

    window.addEventListener("beforeunload", saveSnapshot)

    return () => {
      window.removeEventListener("beforeunload", saveSnapshot)
      saveSnapshot()
    }
  }, [saveSnapshot, storageKey, programStatus])

  if (!initialState) {
    return (
      <Box sx={GRID_STAGE_SX}>
        <LoadingComponent loadedComponents="data" />
      </Box>
    )
  }

  return (
    <Box
      ref={gridWrapperRef}
      sx={{
        ...GRID_STAGE_SX,
        // Anchor for the floating selection bar below.
        position: "relative",
        "& .cell.is-flagged": {
          padding: 0,
          backgroundColor: alpha(appTheme.palette.error.main, 0.15),
        },
        // Table text one step below the app scale — data-dense rows read
        // better at 13.5 (Mayer). Cell renderers use body3 to match.
        "& .MuiDataGrid-cell": { fontSize: 13.5 },
        "& .MuiDataGrid-columnHeaderTitle": { fontSize: 13.5 },
        // The expanded row and its panel are ONE unit: same fill, no divider
        // seam between them — the colour break made them read as two
        // different surfaces (Mayer).
        "& .MuiDataGrid-row--detailPanelExpanded": {
          backgroundColor: "#fafaf7",
        },
        // Doubled class beats MUI's own row:hover specificity — without it
        // hovering the expanded row flashed the grey overlay through the
        // one-unit fill.
        "& .MuiDataGrid-row--detailPanelExpanded.MuiDataGrid-row:hover": {
          backgroundColor: "#fafaf7",
        },
        "& .MuiDataGrid-row--detailPanelExpanded .MuiDataGrid-cell": {
          borderBottom: "none",
        },
      }}
    >
      <FilterErrorBoundary>
        <DataGridPro
          // isPureMentor is part of the key: the user record can arrive after
          // first mount, and the role-aware defaults only apply at mount time.
          key={`${programStatus}:${isPureMentor}`}
          rows={data ?? []}
          loading={loading}
          apiRef={apiRef}
          columns={[
            checkboxColumn,
            // Declared explicitly so the column ORDER is definitive: left
            // implicit, MUI prepends the toggle before the checkbox, while
            // the pinned-left list says checkbox first — header and body
            // cells then disagreed about which column sat where (Mayer's
            // misaligned checkboxes).
            { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF },
            ...buildStudentsTableColumns(programStatus),
            actionsColumn,
          ]}
          getRowId={(row) => row.studentId}
          getDetailPanelContent={(params) => (
            <StudentDetailPanel student={params.row} />
          )}
          // Deterministic, not "auto": the auto-height panel re-measures at
          // the virtualization boundary, which collapses rows to 0x0 and
          // changes scrollHeight mid-scroll (real CLS every time the expanded
          // row crosses the render window). This formula is the SAME budget
          // the panel styles itself with (480px floor, 280px page chrome) —
          // keep the two in sync.
          getDetailPanelHeight={() => Math.max(480, window.innerHeight - 280)}
          detailPanelExpandedRowIds={
            expandedStudentId ? [expandedStudentId] : []
          }
          onDetailPanelExpandedRowIdsChange={(ids) =>
            onExpandedStudentChanged(
              ids.length > 0 ? String(ids[ids.length - 1]) : null,
            )
          }
          onRowClick={(params) => {
            if (suppressRowClickRef.current) {
              return
            }
            apiRef.current?.toggleDetailPanel(params.id)
          }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(model) =>
            setSelectedIds(model.map(String))
          }
          onFilterModelChange={(model) => onGridFilterChanged(model)}
          // No in-grid toolbar row: the grid's controls live on the page's
          // filter-and-actions row (the Filter and Export controls). The
          // filter panel they open anchors back onto that row.
          slots={studentsGridIconSlots}
          slotProps={{
            // The filter panel anchors under the Filter control, which now
            // leads the filter-and-actions row on the left — open it flush to
            // that control's left edge and extend right, so it never drifts
            // off the viewport.
            panel: panelAnchorEl
              ? { anchorEl: panelAnchorEl, placement: "bottom-start" }
              : {},
            // The MUI filter panel is retired (the page's field pickers are
            // the one filter editor) — the column menu must not resurrect it.
            columnMenu: { slots: { columnMenuFilterItem: null } },
          }}
          sx={{
            border: "none",
          }}
          // Two-line Name cells (name over email) need headroom over the 52px
          // default; 54 harvests the 2026-07 type-scale step-down (body2
          // 17→15.5) without the cells touching the dividers.
          rowHeight={54}
          pageSizeOptions={[10, 25, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            pinnedColumns: {
              // The implicit checkbox and detail-toggle columns must be pinned
              // ahead of Name, or every value shifts one header to the right
              // on pinned grids.
              left: [
                GRID_CHECKBOX_SELECTION_FIELD,
                GRID_DETAIL_PANEL_TOGGLE_FIELD,
                ColumnKeys.NAME,
              ],
              right: [ColumnKeys.ACTIONS],
            },
            columns: {
              columnVisibilityModel: Object.fromEntries(
                tabDefaults.hiddenColumns.map((field) => [field, false]),
              ),
            },
            sorting: {
              sortModel: tabDefaults.sortModel,
            },
            ...initialState,
          }}
        />
      </FilterErrorBoundary>
      {/* The floating selection bar (the attendance painter-bar idiom):
          bottom-center overlay of the grid, mounted only while rows are
          selected. The email count is the contract — the button copies
          exactly the addresses the selection holds, selected rows without
          an email don't inflate the label. */}
      {selectedIds.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            bottom: "12px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: (theme) => theme.zIndex.modal,
            px: "16px",
            py: "8px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            maxWidth: "calc(100% - 32px)",
          }}
        >
          <Typography variant="body2_sb" sx={{ whiteSpace: "nowrap" }}>
            {selectedIds.length} selected
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={selectedEmails.length === 0}
            onClick={copySelectedEmails}
            sx={{ whiteSpace: "nowrap" }}
          >
            {copiedCount !== null
              ? `Copied ${copiedCount} email${copiedCount === 1 ? "" : "s"}`
              : `Copy ${selectedEmails.length} email${
                  selectedEmails.length === 1 ? "" : "s"
                }`}
          </Button>
          <Button
            size="small"
            onClick={() => apiRef.current.setRowSelectionModel([])}
            sx={{ whiteSpace: "nowrap" }}
          >
            Clear
          </Button>
        </Paper>
      )}
    </Box>
  )
}
